import { unByKey } from 'ol/Observable';

import { CLICKABLE, FEATURE_INFO, LEGEND, TREE } from './layer_capabilities';

const LAYER_KEY = 'layer';
const NAME_KEY = 'layer_name';
const ICON_KEY = 'layer_icon';
const FILTER_ACTIVE_KEY = 'layer_filter';
const LEGEND_URL_KEY = 'legend_url';
const LEGEND_VISIBLE_KEY = 'legend_visible';
const FEATURE_INFO_ACTIVE_KEY = 'feature_info_active';
const LAYER_CAPABILITIES_KEY = 'capabilities';
const LEGEND_CLICK_HANDLER_KEY = 'legend_click_handler';
const LAYER_EXPANDED_KEY = 'layer_expanded';
const LAYER_EXCLUSIVE_KEY = 'layer_exclusive';
const LAYER_ERROR_KEY = 'layer_error';
const TIME_KEY = 'layer_time';

const isLayerStateChange = key => {
    return key == FILTER_ACTIVE_KEY || key === LEGEND_VISIBLE_KEY || key === FEATURE_INFO_ACTIVE_KEY || key === LAYER_ERROR_KEY || key === TIME_KEY || key === 'visible' || key === 'opacity';
}

class Layer {

    constructor(layer, name) {
        this.layer = layer;
        this.layer.set(LAYER_KEY, this);
        this.setName(name);
        this.layers = [];
    }

    getId() {
        return this.layer.ol_uid;
    }

    getName() {
        return this.layer.get(NAME_KEY);
    }

    setName(name) {
        this.layer.set(NAME_KEY, name);
    }
    
    getIcon() {
        return this.layer.get(ICON_KEY);
    }

    setIcon(icon) {
        this.layer.set(ICON_KEY, icon);
    }

    getCapabilities() {
        return this.layer.get(LAYER_CAPABILITIES_KEY);
    }

	setCapabilities(capabilities) {
        const currentCapabilities = this.getCapabilities() || {};
        const target = Object.assign({}, currentCapabilities, capabilities);
        
        this.layer.set(LAYER_CAPABILITIES_KEY, target);
    }
	
	hasCapability(property) {
        const capabilities = this.getCapabilities() || {};
        return capabilities[property] === true;
    }

    isVisible() {
        return this.layer.getVisible();
    }

    setVisible(visible) {
        this.layer.setVisible(visible);
    }

    getOpacity() {
        return this.layer.getOpacity();
    }

    setOpacity(opacity) {
        this.layer.setOpacity(opacity);
    }

    getZIndex() {
        return this.layer.getZIndex();
    }

    setZIndex(zIndex) {
        this.layer.setZIndex(zIndex);
    }

    isLegendVisible() {
        return this.layer.get(LEGEND_VISIBLE_KEY);
    }

    setLegendVisible(visible) {
        this.layer.set(LEGEND_VISIBLE_KEY, visible);
    }
    
    isFilterActive() {
        return this.layer.get(FILTER_ACTIVE_KEY);
    }
    
    setFilterActive(filter) {
        this.layer.set(FILTER_ACTIVE_KEY, filter);
    }

    getTime() {
        return this.layer.get(TIME_KEY);    
    }
    
    setTime(timestamp) {
        return this.layer.set(TIME_KEY, timestamp);
    }
    
    hasLegend(resolution) {
        return this.hasCapability(LEGEND) && this.getLegendUrl(resolution) != null;
    }

	getLegendUrl() {
		return this.layer.get(LEGEND_URL_KEY);
	}
	
	setLegendUrl(legendUrl) {
  		this.layer.set(LEGEND_URL_KEY, legendUrl);
  	}
	
	isClickable() {
        return this.hasCapability(CLICKABLE);        
    }
			
	hasFeatureInfo() {
		return this.hasCapability(FEATURE_INFO);
	}
		
	isFeatureInfoActive() {
		return this.layer.get(FEATURE_INFO_ACTIVE_KEY);
	}
	
	setFeatureInfoActive(active) {
		this.layer.set(FEATURE_INFO_ACTIVE_KEY, active);
		this.layer.changed();
	}
	
	getLayers() {
		return this.layers;
	}
	
	addLayer(layer) {
        this.layers.push(layer);
        
        if( !this.hasCapability(TREE) ) {
            this.setCapabilities( {[TREE]: true});
        }
    }

    isExpanded() {
        return this.layer.get(LAYER_EXPANDED_KEY) || false;
    }

    setExpanded(expanded) {
        this.layer.set(LAYER_EXPANDED_KEY, expanded);
    }
    
    isExclusive() {
        return this.layer.get(LAYER_EXCLUSIVE_KEY) || false;
    }
    
    setExclusive(exclusive) {
        this.layer.set(LAYER_EXCLUSIVE_KEY, exclusive);
    }
	
	changed() {
		this.layer.changed();
		console.log("called changed on ", this.getName());
	}
	
	addAttribution(attribution) {
		const source = this.layer.getSource();
		if( source ) {
			source.setAttributions(attribution);
		}
	}
	
    addChangeLayerStateEventHandler(handler, delay) {
        let timeout = null;

		const layerKey = this.layer.on("propertychange", ({key, oldValue}) => {
            const change = isLayerStateChange(key);
            
			if( change && delay ) {
				clearTimeout(timeout)
				timeout = setTimeout(() => handler(key), delay);
			} else if( change ) {
                handler(key);
            }
        });
        
        return () => {
            unByKey(layerKey);
        }
    }

    getLegendClickHandler() {
        return this.layer.get(LEGEND_CLICK_HANDLER_KEY);
    }

	setLegendClickHandler(handler) {
		if( handler != null ) {
			this.layer.set(LEGEND_CLICK_HANDLER_KEY, handler);
			
			return () => {
				this.layer.set(LEGEND_CLICK_HANDLER_KEY, null);
			}
		}
	}
        
    setLayerError(message) {        
        this.layer.set(LAYER_ERROR_KEY, message);
    }
    
    isLayerError() {
        return !!this.layer.get(LAYER_ERROR_KEY);
    }
    	
}

export { Layer, NAME_KEY, LAYER_KEY, FILTER_ACTIVE_KEY, LEGEND_URL_KEY, LEGEND_VISIBLE_KEY, FEATURE_INFO_ACTIVE_KEY, ICON_KEY, LAYER_ERROR_KEY }